/**
 * 访客端通知：音频 + 独立对话页标签标题
 *
 * 特别注意：
 *   - 嵌入式 widget 不能改宿主页 title，只有独立对话页 (mode='page') 才改
 *   - unlock 时机：打开面板 / textarea focus / 发送消息，多重触发
 */

class NotifyAudio {
  private audio: HTMLAudioElement | null = null;
  private unlocked = false;
  private lastPlayAt = 0;
  private src: string;
  enabled = true;

  constructor(src: string) {
    this.src = src;
  }

  private ensure(): HTMLAudioElement {
    if (!this.audio) {
      this.audio = new Audio(this.src);
      this.audio.preload = 'auto';
      this.audio.volume = 0.8;
    }
    return this.audio;
  }

  async unlock(): Promise<boolean> {
    if (this.unlocked) return true;
    const el = this.ensure();
    const wasMuted = el.muted;
    el.muted = true;
    try {
      await el.play();
      el.pause();
      el.currentTime = 0;
      el.muted = wasMuted;
      this.unlocked = true;
      return true;
    } catch {
      el.muted = wasMuted;
      return false;
    }
  }

  async play(): Promise<void> {
    if (!this.enabled) return;
    const now = Date.now();
    if (now - this.lastPlayAt < 800) return;
    this.lastPlayAt = now;
    const el = this.ensure();
    try {
      el.currentTime = 0;
      await el.play();
    } catch {
      // 权限被拦，等下次 unlock
    }
  }

  isUnlocked(): boolean { return this.unlocked; }
}

class TabTitleAlert {
  private originalTitle = '';
  private isAlerting = false;
  private cleanup: Array<() => void> = [];

  install(): void {
    if (this.originalTitle) return;
    this.originalTitle = document.title;
    const onVis = () => { if (!document.hidden) this.clear(); };
    const onFocus = () => this.clear();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    this.cleanup.push(
      () => document.removeEventListener('visibilitychange', onVis),
      () => window.removeEventListener('focus', onFocus),
    );
  }

  alert(text = '有新消息'): void {
    if (!this.originalTitle) return;
    if (!document.hidden) return;
    if (this.isAlerting) return;
    this.isAlerting = true;
    document.title = `【${text}】${this.originalTitle}`;
  }

  clear(): void {
    if (this.isAlerting) {
      document.title = this.originalTitle;
      this.isAlerting = false;
    }
  }

  uninstall(): void {
    this.clear();
    for (const fn of this.cleanup) fn();
    this.cleanup = [];
    this.originalTitle = '';
  }

  /**
   * 当外部主动修改 document.title 时，同步更新 originalTitle。
   * 用于比如 chat-page 拿到配置后把标题改成 "在线客服-小婷"。
   */
  updateBase(nextTitle: string): void {
    this.originalTitle = nextTitle;
    if (!this.isAlerting) document.title = nextTitle;
  }
}

/** 每个 widget 实例独立创建（widget host 不同则音频路径不同） */
export function createNotifyAudio(host: string): NotifyAudio {
  return new NotifyAudio(`${host}/sounds/visitor.mp3`);
}

export function createTabTitleAlert(): TabTitleAlert {
  return new TabTitleAlert();
}

export type { TabTitleAlert };
